import { useState, useEffect, useCallback, useRef } from 'react';
import API from '@api';
import prepareAllCommentsOptions from '@utils/orderUtils/prepareAllCommentsOptions';
import prepareNewParams from '@utils/orderUtils/prepareNewParams';
import prepareAddressComments from '@utils/orderUtils/prepareAddressComments';
import type { PrepareAllCommentsOptionsReturnType } from '@utils/orderUtils/prepareAllCommentsOptions';

interface UseCommentsLayerOptions {
  selectedAddress?: string | number | null;
  editAddress?: boolean;
  isMainAddressProp?: boolean;
}

export interface CommentsLayerReturn {
  allCommentsOptions: Partial<PrepareAllCommentsOptionsReturnType>;
  isMainAddress: boolean;
  selectMainAddress: () => void;
  changeNewParams: (
    type: string,
    value: boolean | number | string,
    index?: number
  ) => void;
  resetComments: () => void;
}

// Factory avoids shared object references that could be accidentally mutated
const getDefaultOptions = (): Partial<PrepareAllCommentsOptionsReturnType> => ({
  orderOptions: [],
  checkboxes: [],
  selects: [],
  orderChange: {},
  orderHome: {}
});

export const useCommentsLayer = ({
  selectedAddress,
  editAddress,
  isMainAddressProp = false
}: UseCommentsLayerOptions): CommentsLayerReturn => {
  const [allCommentsOptions, setAllCommentsOptions] =
    useState<Partial<PrepareAllCommentsOptionsReturnType>>(getDefaultOptions);
  const [isMainAddress, setIsMainAddress] = useState(isMainAddressProp);

  const allCommentsOptionsRef = useRef<
    Partial<PrepareAllCommentsOptionsReturnType>
  >(getDefaultOptions());
  const commentsPartialRef = useRef<PrepareAllCommentsOptionsReturnType | null>(
    null
  );
  const prevSelectedAddressRef = useRef(selectedAddress);
  const prevEditAddressRef = useRef(editAddress);
  // Tracks in-flight address requests; stale responses are ignored when the id doesn't match
  const addressRequestIdRef = useRef(0);
  // Skips the first run of the update effect (mount is handled by the dedicated mount effect)
  const isFirstUpdateRunRef = useRef(true);

  const updateAllCommentsOptions = useCallback(
    (next: Partial<PrepareAllCommentsOptionsReturnType>) => {
      allCommentsOptionsRef.current = next;
      setAllCommentsOptions(next);
    },
    []
  );

  const changeNewParams = useCallback(
    (type: string, value: boolean | number | string, index?: number) => {
      const newState = prepareNewParams({
        ...allCommentsOptionsRef.current,
        type,
        value,
        index: index ?? 0
      });
      updateAllCommentsOptions({
        ...allCommentsOptionsRef.current,
        ...newState
      });
    },
    [updateAllCommentsOptions]
  );

  const getInitialComments = useCallback(
    async (withoutAddress?: boolean) => {
      const res1 = await API.getCommentsToOrder({});

      let newState = prepareAllCommentsOptions({
        data: res1.getCommentList,
        currentState:
          allCommentsOptionsRef.current as PrepareAllCommentsOptionsReturnType,
        withoutAddress
      });

      if (!withoutAddress && !commentsPartialRef.current) {
        commentsPartialRef.current = newState;
      }

      const res2 = await API.getOrderComments({});
      res2.getOrderComments?.commentItems?.forEach((item: any) => {
        if (item.comment.type === 'checkbox') {
          newState.checkboxes?.forEach((chk: any, j: number) => {
            if (item.comment.id === chk.id && chk.attachment === 'order') {
              const updated = prepareNewParams({
                ...newState,
                type: 'checkboxes',
                value: item.value === '1',
                index: j
              });
              newState = { ...newState, ...updated };
            }
          });
        }
      });

      updateAllCommentsOptions(newState);
    },
    [updateAllCommentsOptions]
  );

  const getAddressComments = useCallback(
    async (addressId: string | number) => {
      // Increment before the async call so any concurrent older call can detect it's stale
      const requestId = ++addressRequestIdRef.current;
      const partial = commentsPartialRef.current ?? getDefaultOptions();
      updateAllCommentsOptions(partial);

      const res = await API.getAddressComments({ addressId: +addressId });

      // Discard response if a newer request has already started
      if (requestId !== addressRequestIdRef.current) return;

      if (res.getAddressComments?.commentItems?.length) {
        // Read ref.current here (not at call-site) to get the state after the reset above
        const newState = prepareAddressComments({
          items: res.getAddressComments.commentItems,
          ...(allCommentsOptionsRef.current as PrepareAllCommentsOptionsReturnType)
        });
        updateAllCommentsOptions(newState);
      }
    },
    [updateAllCommentsOptions]
  );

  const resetComments = useCallback(() => {
    updateAllCommentsOptions(commentsPartialRef.current ?? getDefaultOptions());
  }, [updateAllCommentsOptions]);

  const selectMainAddress = useCallback(() => {
    setIsMainAddress(prev => !prev);
  }, []);

  useEffect(() => {
    setIsMainAddress(isMainAddressProp);
  }, [isMainAddressProp]);

  // Mount-only: fetch initial data
  useEffect(() => {
    getInitialComments().then(() => {
      if (selectedAddress) getAddressComments(selectedAddress);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Runs on subsequent dep changes only (first run is skipped via isFirstUpdateRunRef)
  useEffect(() => {
    if (isFirstUpdateRunRef.current) {
      isFirstUpdateRunRef.current = false;
      return;
    }

    const prevSel = prevSelectedAddressRef.current;
    const prevEdit = prevEditAddressRef.current;
    prevSelectedAddressRef.current = selectedAddress;
    prevEditAddressRef.current = editAddress;

    if (selectedAddress !== prevSel || (editAddress && !prevEdit)) {
      getInitialComments().then(() => {
        if (selectedAddress) getAddressComments(selectedAddress);
      });
    }
  }, [selectedAddress, editAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    allCommentsOptions,
    isMainAddress,
    selectMainAddress,
    changeNewParams,
    resetComments
  };
};

export default useCommentsLayer;
